import { useState } from 'react';
import type { FormEvent } from 'react';
import { ShieldAlert, ShieldCheck, ShieldQuestion, Search, Truck, Store } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Field, Input } from '@/components/ui/Field';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../nav';

interface FraudResult {
  courier: string;
  display_name: string;
  ok: boolean;
  total?: number;
  delivered?: number;
  returned?: number;
  success_ratio?: number;
  error?: string;
}

const ERROR_LABEL: Record<string, string> = {
  not_configured: 'Not set up yet — ask ERP to add credentials in Delivery Merchants',
  auth_failed: 'Could not log in — check the stored credentials',
  lookup_failed: 'Courier did not return data for this number',
  network_error: 'Could not reach the courier',
  invalid_phone: 'Invalid phone number',
};

function ratingFor(ratio: number): { label: string; tone: 'success' | 'warning' | 'error' } {
  if (ratio >= 90) return { label: 'Excellent', tone: 'success' };
  if (ratio >= 70) return { label: 'Good', tone: 'warning' };
  return { label: 'Risky', tone: 'error' };
}

interface OrderSummary {
  total_orders: number;
  delivered_count: number;
  cancelled_count: number;
  shop_orders: number;
  delivery_orders: number;
  last_order_at: string | null;
}

// Checks a phone number against each courier's OWN platform-wide delivery
// history (Pathao's real merchant API, Steadfast's website session) —
// not just our own orders, which is far too small a sample on its own to
// mean anything. See thekseoul-admin-hub's core-check-customer-fraud
// edge function (shared with ERP's identical tool).
export default function AdminFraudCheck() {
  const { push } = useToast();
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<FraudResult[] | null>(null);
  const [orderSummary, setOrderSummary] = useState<OrderSummary | null>(null);

  const handleCheck = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = phone.trim();
    if (!trimmed) return;
    setLoading(true);
    setResults(null);
    setOrderSummary(null);
    try {
      const [fraudRes, summaryRes] = await Promise.all([
        supabase.functions.invoke('core-check-customer-fraud', { body: { phone: trimmed } }),
        supabase.rpc('core_customer_order_summary', { p_phone: trimmed }),
      ]);
      if (fraudRes.error) throw fraudRes.error;
      if (!fraudRes.data?.ok) throw new Error(fraudRes.data?.error ?? 'Lookup failed');
      setResults(fraudRes.data.results as FraudResult[]);
      if (!summaryRes.error) setOrderSummary((summaryRes.data as OrderSummary[])?.[0] ?? null);
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to check this number');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="max-w-2xl lg:max-w-4xl space-y-5">
        <div>
          <h1 className="text-[19px] font-bold text-ink">Fraud Check</h1>
          <p className="text-[12.5px] text-ink-faint">
            Look up a phone number's delivery history directly from Pathao/Steadfast — across their whole merchant network, not just our own orders.
            Use this before confirming a COD order from an unfamiliar number.
          </p>
        </div>

        <Card>
          <CardBody>
            <form onSubmit={handleCheck} className="flex items-end gap-2.5">
              <div className="flex-1">
                <Field label="Customer's Phone">
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="017XXXXXXXX" required />
                </Field>
              </div>
              <Button type="submit" loading={loading}>
                <Search className="w-4 h-4" /> Check
              </Button>
            </form>
          </CardBody>
        </Card>

        {results && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.map((r) => (
              <Card key={r.courier} className="overflow-hidden">
                <CardHeader title={r.display_name} icon={<Truck className="w-4 h-4" />} />
                <CardBody>
                  {!r.ok ? (
                    <EmptyState icon={<ShieldQuestion className="w-5 h-5" />} title={ERROR_LABEL[r.error ?? ''] ?? 'Could not check this courier'} />
                  ) : (
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="rounded-xl bg-surface-muted py-2.5">
                          <p className="text-[10.5px] uppercase tracking-wide text-ink-faint">Processed</p>
                          <p className="text-[18px] font-bold text-ink tabular-nums">{r.total}</p>
                        </div>
                        <div className="rounded-xl bg-success-bg py-2.5">
                          <p className="text-[10.5px] uppercase tracking-wide text-success">Delivered</p>
                          <p className="text-[18px] font-bold text-success tabular-nums">{r.delivered}</p>
                        </div>
                        <div className="rounded-xl bg-warning-bg py-2.5">
                          <p className="text-[10.5px] uppercase tracking-wide text-warning">Returned</p>
                          <p className="text-[18px] font-bold text-warning tabular-nums">{r.returned}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-1">
                        <span className="text-[12px] text-ink-faint">Success ratio</span>
                        <Badge tone={ratingFor(r.success_ratio ?? 0).tone}>
                          {r.success_ratio}% — {ratingFor(r.success_ratio ?? 0).label}
                        </Badge>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            ))}

            <Card className="overflow-hidden">
              <CardHeader title="Our Order History" icon={<Store className="w-4 h-4" />} />
              <CardBody>
                {!orderSummary || orderSummary.total_orders === 0 ? (
                  <EmptyState icon={<ShieldQuestion className="w-5 h-5" />} title="No orders found with us" />
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="rounded-xl bg-surface-muted py-2.5">
                        <p className="text-[10.5px] uppercase tracking-wide text-ink-faint">Total</p>
                        <p className="text-[18px] font-bold text-ink tabular-nums">{orderSummary.total_orders}</p>
                      </div>
                      <div className="rounded-xl bg-success-bg py-2.5">
                        <p className="text-[10.5px] uppercase tracking-wide text-success">Delivered</p>
                        <p className="text-[18px] font-bold text-success tabular-nums">{orderSummary.delivered_count}</p>
                      </div>
                      <div className="rounded-xl bg-warning-bg py-2.5">
                        <p className="text-[10.5px] uppercase tracking-wide text-warning">Cancelled</p>
                        <p className="text-[18px] font-bold text-warning tabular-nums">{orderSummary.cancelled_count}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-1 text-[12px]">
                      <span className="text-ink-faint">From shop / in-store</span>
                      <span className="font-semibold text-ink tabular-nums">{orderSummary.shop_orders}</span>
                    </div>
                    <div className="flex items-center justify-between text-[12px]">
                      <span className="text-ink-faint">Delivery orders</span>
                      <span className="font-semibold text-ink tabular-nums">{orderSummary.delivery_orders}</span>
                    </div>
                    {orderSummary.last_order_at && (
                      <div className="flex items-center justify-between pt-1 mt-1 border-t border-border-subtle text-[12px]">
                        <span className="text-ink-faint">Last order</span>
                        <span className="font-semibold text-ink">{orderSummary.last_order_at.slice(0, 10)}</span>
                      </div>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
          </div>
        )}

        {!results && !loading && (
          <div className="flex items-center gap-2 text-[12px] text-ink-faint">
            <ShieldCheck className="w-4 h-4" /> Enter a phone number above to check its delivery history.
          </div>
        )}
        {!results && loading && (
          <div className="flex items-center gap-2 text-[12px] text-ink-faint">
            <ShieldAlert className="w-4 h-4 animate-pulse" /> Checking with couriers — this can take a few seconds…
          </div>
        )}
      </div>
    </AppShell>
  );
}
